/**
 * AboutBlock - Bloc informatif "À propos de RISK-DATA"
 * Affiché sous le formulaire de recherche sur la HomePage
 * Contenu conforme au CDC Juridique v1.2 §9.2
 *
 * Version accordéon : replié par défaut pour réduire le scroll
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardBody } from '../ui/Card'
import Alert from '../ui/Alert'

export default function AboutBlock() {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <Card className="max-w-2xl mx-auto bg-gray-50">
      <CardBody className="p-4">
        {/* En-tête cliquable */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg -m-1 p-1"
          aria-expanded={isExpanded}
          aria-controls="about-content"
        >
          <span className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-blue-600 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="font-semibold text-gray-900">
              À propos de RISK-DATA
            </span>
          </span>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Version repliée : résumé compact */}
        {!isExpanded && (
          <p className="text-sm text-gray-500 mt-2 pl-7">
            Outil d'aide à la recherche toxicologique pour professionnels de la prévention.
            <span className="text-amber-600 ml-1 font-medium">
              Information importante disponible.
            </span>
          </p>
        )}

        {/* Contenu déplié */}
        {isExpanded && (
          <div id="about-content" className="mt-4 space-y-4">
            {/* Paragraphe 1 */}
            <p className="text-sm text-gray-700">
              RISK-DATA est un outil d'aide à la recherche destiné aux professionnels
              de la prévention des risques chimiques (médecins du travail, préventeurs,
              responsables HSE, IPRP).
            </p>

            {/* Paragraphe 2 */}
            <p className="text-sm text-gray-700">
              Notre plateforme agrège et centralise des données toxicologiques issues
              de sources réglementaires officielles françaises et européennes : INRS,
              ANSES, ECHA et AGRITOX. Elle facilite vos recherches et la constitution
              de vos Documents Uniques d'Évaluation des Risques Professionnels (DUERP).
            </p>

            {/* Bloc avertissement */}
            <Alert variant="warning" title="Information importante">
              <p className="text-sm">
                Les informations présentées sur RISK-DATA sont fournies à titre indicatif
                et ne constituent en aucun cas un conseil personnalisé. RISK-DATA ne se
                substitue pas aux publications officielles des organismes sources, qui
                font seules foi.
              </p>
              <p className="text-sm mt-2">
                Les données sont mises à jour périodiquement mais ne constituent pas une
                veille réglementaire exhaustive ni en temps réel. L'utilisateur professionnel
                doit systématiquement vérifier l'actualité des informations auprès des
                sources primaires avant toute utilisation.
              </p>
            </Alert>

            {/* Liens */}
            <div className="flex flex-wrap gap-4 text-sm pt-2">
              <Link
                to="/cgu"
                className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
              >
                Consulter les CGU
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
              <a
                href="#sources-officielles"
                className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
              >
                Accéder aux sources officielles
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </a>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
